import { useAuth } from "@clerk/clerk-react";
import { Grid, Slide, useTheme } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  ENTER_DELAY,
  EXIT_DELAY,
  SlideAnimationContext,
  SlideAnimationProvider,
} from "contexts/SlideAnimation";
import { SplashPage, SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  DiscoverySource,
  OnboardAction,
  OrganizationProProfileStatus,
  SearchParams,
  VAStatus,
} from "schemas/dashboard";
import { Map } from "schemas/functions";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";

import Loading from "components/Loading";
import { PAGE_VISITED } from "constants/trackingProps";
import EditTemplateDialog from "features/Influencer/Templates/TemplatesTable/EditTemplate";
import { fetcherAuth } from "utils/api";
import { getSource, identifyUser, trackEvent } from "utils/tracking";
import { useGamification } from "utils/useGamification";
import { useSubscription } from "utils/useSubscription";
import { useTracking } from "utils/useTracking";

import StandardPreferenceQuestion from "../InstagramOnboarding/Preference/standard";
import AboutForm from "./AboutForm";
import CategoryForm from "./CategoryForm";
import OnboardingPaywallPage from "./Paywall";
import { getOnboardingPage, getOnboardingSteps } from "./helper";
import styles from "./styles";

interface SaveOnboardingOptions {
  setOnboardingForm?: boolean;
  showButtonLoading?: boolean;
}

const OnboardingInfluencer = () => {
  const {
    currentOrg,
    currentUser,
    setCurrentUser,
    setCurrentOrg,
    setOrganizationVaProfile,
    setOrganizationProProfile,
    setUserNotifications,
    setUserIntegrations,
    setEmailSettings,
    setHideCityDialog,
    setProfile,
    setIncludedBentoSearchFilterIds,
    setExcludedBentoSearchFilterIds,
    setShowGamificationProfile,
    showGamificationProfile,
    gamificationProfile,
  } = useContext(OrganizationUserContext);
  const { editTemplateDialogId, setEditTemplateDialogId } = useContext(
    OutreachTemplatesContext,
  );
  const theme = useTheme();
  const { setSubscription } = useContext(SubscriptionContext);
  const [searchParams] = useSearchParams();
  const { handleCheckout } = useSubscription();

  const { slideOutUp, slideInUp, slideOutDown, slideInDown, slideProps } =
    useContext(SlideAnimationContext);
  const { trackUserHasOnboarded } = useTracking();

  const referralCode = searchParams.get(SearchParams.REFERRAL_CODE);
  const affiliateCode = searchParams.get(SearchParams.AFFILIATE_CODE);

  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken, signOut } = useAuth();

  const [fetchUserLoading, setFetchUserLoading] = useState(true);
  const source = getSource();
  const fromSocial =
    source === DiscoverySource.instagram || source === DiscoverySource.tiktok;
  const { fetchGamificationProfile } = useGamification();

  const [onboardingForm, setOnboardingForm] = useState<Map>({
    name: currentUser?.name || "",
    location: "",
    brands: [],
    wishlistBrands: [],
    profileTypes: [],
    incomeLevel: "",
    pastBrandValidation: null,
    primarySocialMediaPlatform: "instagram",
    defaultTemplateHighlights: [],

    // These fields are auto-filled when user sign up
    email: "",
    organizations: [],
    organizationId: "",
    organizationName: "",
    type: "influencer",
  });

  const fetchUserInfo = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/auth/user?affiliate_code=${affiliateCode || ""}`,
      );
      identifyUser(res.organizationUser.id);
      setCurrentUser(res.organizationUser);
      setCurrentOrg(res.organization);
      setOrganizationVaProfile(res.organizationVaProfile);
      setOrganizationProProfile(res.organizationProProfile);
      setSubscription(res.subscription);
      setUserIntegrations(res.userIntegrations);
      setUserNotifications(res.userNotifications);
      setEmailSettings(res.emailSetting);
      setIncludedBentoSearchFilterIds(res.includedBentoSearchFilterIds);
      setExcludedBentoSearchFilterIds(res.excludedBentoSearchFilterIds);
      setProfile(res.organizationProfile);
      setFetchUserLoading(false);
      setShowGamificationProfile(res.showGamificationProfile);
      if (
        res.organization.vaStatus === VAStatus.pending &&
        res.organization.isVaUser
      ) {
        navigate(`/${routes.onboardingVaSelfServe}${location.search}`);
        return;
      }
      if (
        res.organization.isProUser &&
        res.organizationProProfile?.status ===
          OrganizationProProfileStatus.pending
      ) {
        navigate(`/${routes.onboardingPro}${location.search}`);
        return;
      }

      // Edge case: user who subscribed to monthly VA and click on the Connect to Google link
      const openIntegrationDialog =
        searchParams.get("open_integration_dialog") === "true";

      // Edge case: user who subscribed to monthly VA and click on Import from Gmail link.
      const openImportGmail =
        searchParams.get("ref") === "va_subscribed_monthly";

      const upgradeToVaFromBannerLink = searchParams.get("va_banner_clicked");

      if (openImportGmail && !openIntegrationDialog) {
        navigate(`/${routes.trackingView}${location.search}`);
        return;
      }

      if (openIntegrationDialog || upgradeToVaFromBannerLink) {
        navigate(`/${routes.home}/all${location.search}`);
        return;
      }

      setHideCityDialog(true);
      if (res.organizationProfile) {
        setOnboardingForm((prev) => ({
          ...prev,
          ...res.organizationProfile,
          name: res.organizationUser.name,
          pitchEmail: res.organizationProfile.pitchEmail,
        }));
      } else {
        setOnboardingForm((prev) => ({
          ...prev,
          name: res.organizationUser.name,
        }));
      }
      slideInUp();
      goToFirstPage(res.organization.id);
    } catch (error) {
      handleClerkUserSignUp();
    }
  };

  const handleClerkUserSignUp = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/auth/signup`,
        "POST",
        {},
        {
          referralCode,
          affiliateCode,
          source: referralCode ? "referral" : source,
        },
      );
      identifyUser(res.organizationUser.id);
      setCurrentUser(res.organizationUser);
      setCurrentOrg(res.organization);
      setOnboardingForm((prev) => ({
        ...prev,
        name: res.organizationUser.name,
      }));
      setShowGamificationProfile(res.showGamificationProfile);

      setHideCityDialog(true);
      goToFirstPage(res.organization.id);
      slideInUp();
      if (
        // @ts-ignore
        typeof window.fbq !== "undefined" &&
        // @ts-ignore
        typeof window.fbq === "function"
      ) {
        // @ts-ignore
        window.fbq("track", "CompleteRegistration");
      }
      if (
        // @ts-ignore
        typeof window.ttq !== "undefined" &&
        // @ts-ignore
        typeof window.ttq.track === "function"
      ) {
        // @ts-ignore
        window.ttq.track("CompleteRegistration");
      }
      if (
        // @ts-ignore
        typeof window.gtag !== "undefined" &&
        // @ts-ignore
        typeof window.gtag === "function"
      ) {
        // @ts-ignore
        window.gtag("event", "conversion", {
          send_to: "AW-18111742383/0PfTCJuSlaIcEK-DrbxD",
        });
      }
    } catch (error) {
      signOut();
      navigate(`/${routes.signIn}`);
      setErrorAlert(error);
    } finally {
      setFetchUserLoading(false);
    }
  };

  const handleBack = async () => {
    await slideOutDown();
    slideInDown();

    trackEvent("Onboarding Form - Back Button Pressed", {
      page: getOnboardingPage(location.pathname),
      route: location.pathname,
    });

    const { steps, currentStep } = getOnboardingSteps(location.pathname);

    if (currentStep < 2) {
      goToFirstPage(currentOrg?.id!);
    } else {
      navigate(`/${steps[currentStep - 2]}${location.search}`);
    }
  };

  const handleSkip = async () => {
    await slideOutUp();

    trackEvent("Onboarding Form - Skip Button Pressed", {
      page: getOnboardingPage(location.pathname),
      route: location.pathname,
    });

    if (location.pathname.endsWith(routes.aboutOnboarding)) {
      // save the name if they skip
      saveOnboardingForm({ name: currentUser?.name });
    }

    if (location.pathname.endsWith(routes.categoryOnboarding)) {
      trackUserHasOnboarded(OnboardAction.FINAL_OPT_IN);
      if (showGamificationProfile && !gamificationProfile) {
        await fetchGamificationProfile(true);
      }
    }
    moveNext();
  };

  const handleNext = async (updatedForm?: Map) => {
    if (updatedForm) {
      setOnboardingForm(updatedForm);
    }
    trackEvent("Onboarding Form - Next Button Pressed", {
      page: getOnboardingPage(location.pathname),
      route: location.pathname,
    });

    if (location.pathname.endsWith(routes.categoryOnboarding)) {
      trackUserHasOnboarded(OnboardAction.FINAL_OPT_IN);
      if (showGamificationProfile && !gamificationProfile) {
        await fetchGamificationProfile(true);
      }
      navigate(`/${routes.home}`);
      return;
    }

    saveOnboardingForm(updatedForm || onboardingForm);
  };

  const moveNext = async () => {
    const { steps, currentStep } = getOnboardingSteps(location.pathname);

    if (currentStep === -1 || currentStep === steps.length) {
      navigate(`/${routes.home}${location.search}`);
    } else {
      navigate(`/${steps[currentStep]}${location.search}`);
    }
    slideInUp();
  };

  const goToFirstPage = (currentOrgId: number) => {
    trackEvent("Onboarding Flow Assigned", {
      flow: "Default",
    });

    navigate(`/${routes.aboutOnboarding}${location.search}`);
  };

  const saveOnboardingForm = async (
    onboardingForm: Map,
    options?: SaveOnboardingOptions,
  ) => {
    await moveNext();
    const previousRoute = location.pathname;

    const onboardingFormReq: Map = {
      ...onboardingForm,
      signature: null, // do not overwrite default signature so that it updates using social links
      source: referralCode ? "referral" : source,
      page: previousRoute,
    };
    if (fromSocial && !onboardingFormReq?.discoverySource) {
      onboardingFormReq.discoverySource = getSource();
    }
    try {
      const { organizationProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        onboardingFormReq,
      );

      setProfile({ ...organizationProfile });

      if (options?.setOnboardingForm) {
        setOnboardingForm((prev) => ({ ...prev, ...organizationProfile }));
      }

      if (options?.showButtonLoading) {
        await moveNext();
      }
    } catch (error) {
      if (error?.message?.includes("location")) {
        setOnboardingForm((prev) => ({
          ...prev,
          location: undefined,
          latitude: undefined,
          longitude: undefined,
          city: undefined,
          address_components: undefined,
        }));
      }
      if (!options?.showButtonLoading) {
        await slideOutDown();
        slideInDown();
        navigate(`${previousRoute}${location.search}`);
      }
      setAlert(
        error?.message ||
          "Unable to save your information. Please reload and try again",
        "error",
      );
    }
  };

  const handleCheckoutVa = () => {
    handleCheckout(
      Plans.VA,
      window.location.pathname,
      source,
      SplashPage.instagram,
    );
  };

  useEffect(() => {
    fetchUserInfo();
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return fetchUserLoading ? (
    <Loading />
  ) : (
    <>
      <Grid container sx={styles.root}>
        <Slide
          in={slideProps.in}
          direction={slideProps.direction}
          easing={{
            enter: theme.transitions.easing.easeInOut,
            exit: theme.transitions.easing.easeIn,
          }}
          timeout={{
            enter: ENTER_DELAY,
            exit: EXIT_DELAY,
          }}
        >
          <Grid sx={styles.formContainer} item xs={12}>
            <Routes>
              <Route
                path="/preferences/*"
                element={
                  <StandardPreferenceQuestion
                    handleCheckoutVa={handleCheckoutVa}
                  />
                }
              />
              <Route
                path="/paywall/*"
                element={
                  <OnboardingPaywallPage
                    handleSkip={handleSkip}
                    moveNext={moveNext}
                  />
                }
              />
              <Route
                path="/about/*"
                element={
                  <AboutForm
                    onboardingForm={onboardingForm}
                    setOnboardingForm={setOnboardingForm}
                    handleNext={handleNext}
                    handleSkip={handleSkip}
                    handleBack={undefined}
                    hideDiscoveryQuestion={Boolean(referralCode) || fromSocial}
                  />
                }
              />
              <Route
                path="/category/*"
                element={
                  <CategoryForm
                    onboardingForm={onboardingForm}
                    handleBack={handleBack}
                    handleSkip={handleSkip}
                    moveNext={() => {
                      trackEvent("Onboarding Form - Next Button Pressed", {
                        page: getOnboardingPage(location.pathname),
                        route: location.pathname,
                      });

                      moveNext();
                    }}
                  />
                }
              />
              <Route
                path="*"
                element={
                  <Navigate
                    to={`/${routes.aboutOnboarding}${location.search}`}
                    replace
                  />
                }
              />
            </Routes>
          </Grid>
        </Slide>
      </Grid>
      {editTemplateDialogId && (
        <EditTemplateDialog
          propsTemplateId={editTemplateDialogId}
          propsHandleClose={() => {
            setEditTemplateDialogId(null);
          }}
        />
      )}
    </>
  );
};

export default function Onboarding() {
  return (
    <SlideAnimationProvider>
      <OnboardingInfluencer />
    </SlideAnimationProvider>
  );
}
